import { usePracticeScope } from '../../../../onboarding/practice/practiceScope';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../lib/utils';
import {
  borderPrimary,
  primaryText,
  secondaryText,
  tertiaryText,
  semiBoldText,
  smallTextSize,
  lightGreenBG,
  greenTextColor,
  whiteBG,
  selectableName,
} from '../../../../../styling/stylingHelper';
import { getPortionsUnitDisplay, formatSubrecipeUnitPrice } from '../../../../../utils/ingredientGrouping';

/** Which list the user is picking from. Revenue items is the default. */
export type SetupTab = 'revenue' | 'subrecipe';

export interface RevenueItemCandidate {
  name: string;
  qty30: number;
  lastSold: Date;
  score: number;
}

export interface SubrecipeCandidate {
  id: string;
  name: string;
  portions: number;
  portionsUnit: string;
  ingredientCount: number;
  /** Live cost per portion / g / ml. */
  unitCost: number;
  /** Dishes that reach this subrecipe, directly or through a parent subrecipe. */
  usedInDishes: number;
  score: number;
}

interface Props {
  /** Counted/report name of the product being set up, shown in the context card. */
  contextName: string;
  /** The order-history product name, when it differs from `contextName`. */
  linkedProductName?: string;
  /** True once we know the subject can't be resolved — setup can't proceed. */
  showNotFound: boolean;
  /** The subject is a counted subrecipe rather than a purchased product (changes the copy). */
  isSubrecipeSubject?: boolean;
  isLoading: boolean;
  /** Subrecipes load separately — without this the tab flashes "no subrecipes found". */
  isLoadingSubrecipes: boolean;

  activeTab: SetupTab;
  onTabChange: (tab: SetupTab) => void;
  revenueSelectedCount: number;
  subrecipeSelectedCount: number;

  // --- Revenue items tab ---
  search: string;
  onSearchChange: (value: string) => void;
  categoryFilter: string;
  onCategoryFilterChange: (value: string) => void;
  categoryNames: string[];
  /** Already filtered + capped. */
  revenueCandidates: RevenueItemCandidate[];
  revenueMatchCount: number;
  hiddenRevenueCount: number;
  isRevenueSelected: (name: string) => boolean;
  onToggleRevenue: (name: string) => void;
  hasAnalysis: (name: string) => boolean;
  categoryNameFor: (name: string) => string | null;

  // --- Subrecipes tab ---
  subrecipeSearch: string;
  onSubrecipeSearchChange: (value: string) => void;
  /** Already filtered + capped. */
  subrecipeCandidates: SubrecipeCandidate[];
  subrecipeMatchCount: number;
  hiddenSubrecipeCount: number;
  isSubrecipeSelected: (id: string) => boolean;
  onToggleSubrecipe: (id: string) => void;
  isCreateNewSelected: boolean;
  onToggleCreateNew: () => void;
}

const searchIcon = (
  <svg
    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085] dark:text-[#CBD5E1]"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z"
      stroke="currentColor"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const inputClass = cn(
  'w-full pl-10 pr-4 h-11 rounded-lg border outline-none text-[16px] leading-6 font-normal',
  borderPrimary,
  'bg-white dark:bg-[#14344C] dark:border-[#2D4F68]',
  'text-zinc-900 dark:text-zinc-50'
);

const badgeNeutral = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
const badgeGreen = 'bg-[#0E8965]/10 text-[#0E8965] dark:bg-[#0E8965]/20 dark:text-darkGreenColor-dark';
const badgeAmber = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200';
const badgeBase = 'text-[11px] px-1.5 py-0.5 rounded-md whitespace-nowrap font-semibold shrink-0';

/** One pickable row: title/meta on the left, the shared radio indicator on the right. */
const SelectableRow: React.FC<{
  onboardingId?: string;
  isSelected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ isSelected, onClick, children, onboardingId }) => (
  <button
    data-onboarding={onboardingId}
    type="button"
    onClick={onClick}
    className={cn(
      'w-full flex items-center justify-between gap-3 pl-3 pr-4 py-3 rounded-lg border cursor-pointer transition-colors text-left',
      isSelected
        ? 'bg-[#0E89651A] dark:bg-green-900/20 border-[#0E8965] dark:border-green-600'
        : 'bg-[#F9F9FA] dark:bg-zinc-900 border-[#E8E8E9] dark:border-[#1E3A4E] dark:bg-[#0E2B42]'
    )}
  >
    <div className="flex-1 min-w-0">{children}</div>
    <div
      className={cn(
        'flex-shrink-0 w-5 h-5 border flex items-center justify-center transition-colors rounded-full',
        isSelected ? 'border-[#0E8965] bg-[#0E8965]' : 'border-[#D0D5DD] dark:border-zinc-600 bg-white dark:bg-white'
      )}
    >
      {isSelected && (
        <div className="w-4 h-4 rounded-full border-2 border-[#0E8965] dark:border-[#0E8965] bg-white dark:bg-white" />
      )}
    </div>
  </button>
);

/**
 * Step 1 of the setup flow: pick what this product should be linked to. Two tabs,
 * two independent lists (revenue items — the default — and subrecipes); selections
 * from both accumulate and are walked by the same stepper. All state lives in the
 * host so switching tabs, or stepping forward and back, never loses a selection.
 */
const ProductSetupSelectStep: React.FC<Props> = ({
  contextName,
  linkedProductName,
  showNotFound,
  isSubrecipeSubject,
  isLoading,
  isLoadingSubrecipes,
  activeTab,
  onTabChange,
  revenueSelectedCount,
  subrecipeSelectedCount,
  search,
  onSearchChange,
  categoryFilter,
  onCategoryFilterChange,
  categoryNames,
  revenueCandidates,
  revenueMatchCount,
  hiddenRevenueCount,
  isRevenueSelected,
  onToggleRevenue,
  hasAnalysis,
  categoryNameFor,
  subrecipeSearch,
  onSubrecipeSearchChange,
  subrecipeCandidates,
  subrecipeMatchCount,
  hiddenSubrecipeCount,
  isSubrecipeSelected,
  onToggleSubrecipe,
  isCreateNewSelected,
  onToggleCreateNew,
}) => {
  const { t } = useTranslation();
  const practice = usePracticeScope();
  const shortDate = (d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  const tabs: { key: SetupTab; label: string; count: number }[] = [
    { key: 'revenue', label: t('productSetup.tabRevenueItems'), count: revenueSelectedCount },
    { key: 'subrecipe', label: t('productSetup.tabSubrecipes'), count: subrecipeSelectedCount },
  ];

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3 px-4 sm:px-5 pt-1 pb-4">
      {/* Product context — also carries the counted-row name (the header used to
          repeat it as a pill above the title). */}
      <div className={cn('rounded-xl border p-3', borderPrimary, 'bg-gray-50 dark:bg-zinc-900/40')}>
        <div className={cn(smallTextSize, semiBoldText, primaryText, selectableName)}>{contextName}</div>
        {linkedProductName && (
          <div className={cn('text-xs', secondaryText, 'mt-0.5', selectableName)}>
            {t('productSetup.linkedProduct', { name: linkedProductName })}
          </div>
        )}
      </div>

      {showNotFound && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            {t(isSubrecipeSubject ? 'productSetup.prepNotFound' : 'productSetup.notFound')}
          </p>
        </div>
      )}

      {/* Target tabs. Deliberately NOT `TabbedSections`: it collapses to a dropdown
          below a 950px viewport, and this panel is full-width on mobile — a dropdown
          would hide the subrecipe option, which is the one users need to discover. */}
      <div className={cn('flex w-full', smallTextSize, semiBoldText)}>
        {tabs.map((tab, idx) => (
          <button
            key={tab.key}
            data-onboarding={practice ? `inventory-setup-tab-${tab.key}` : undefined}
            type="button"
            onClick={() => onTabChange(tab.key)}
            className={cn(
              'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border-t border-b',
              borderPrimary,
              activeTab === tab.key ? cn(lightGreenBG, greenTextColor) : cn(whiteBG, secondaryText),
              idx === 0 ? cn('rounded-l-xl border-l', borderPrimary) : cn('rounded-r-xl border-r', borderPrimary),
              idx === 0 && 'border-r border-gray-300 dark:border-borderPrimary-dark'
            )}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={cn(badgeBase, activeTab === tab.key ? badgeGreen : badgeNeutral)}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'revenue' ? (
        <>
          {/* Search + category filter for revenue items */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              {searchIcon}
              <input data-onboarding={practice ? "inventory-setup-search" : undefined}
                type="text"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t('productSetup.searchPlaceholder') as string}
                className={inputClass}
              />
            </div>
            {categoryNames.length > 0 && (
              <select
                value={categoryFilter}
                onChange={(e) => onCategoryFilterChange(e.target.value)}
                className={cn(
                  'h-11 px-3 rounded-lg border max-w-[45%] outline-none text-[15px] leading-6 font-normal',
                  borderPrimary,
                  'bg-white dark:bg-[#14344C] dark:border-[#2D4F68]',
                  'text-zinc-900 dark:text-zinc-50'
                )}
              >
                <option value="">{t('productSetup.categoryFilterAll')}</option>
                {categoryNames.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex-1 p-4 rounded-lg border border-[#E8E8E9] dark:border-[#2D4F68] shadow-[0px_1px_1.5px_0.1px_#16192408] dark:shadow-[0px_1px_1.5px_0.1px_#00000040] flex flex-col min-h-0">
            <p className="text-[16px] leading-5 font-semibold text-[#0C111D] dark:text-zinc-50 flex items-center gap-1 mb-3 flex-shrink-0">
              {t('productSetup.revenueItemsHeading')}
              <span>({revenueMatchCount})</span>
            </p>

            {isLoading ? (
              <p className={cn(smallTextSize, tertiaryText, 'py-6 text-center')}>{t('inventory.loading')}</p>
            ) : revenueCandidates.length === 0 ? (
              <p className={cn(smallTextSize, tertiaryText, 'py-6 text-center')}>{t('productSetup.noResults')}</p>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar min-h-0">
                {revenueCandidates.map((c) => {
                  const catName = categoryNameFor(c.name);
                  const exists = hasAnalysis(c.name);
                  return (
                    <SelectableRow
                      onboardingId={practice ? "inventory-setup-pick" : undefined}
                      key={c.name}
                      isSelected={isRevenueSelected(c.name)}
                      onClick={() => onToggleRevenue(c.name)}
                    >
                      {/* Wraps: a long dish name takes the full width and pushes the badges
                          to the next line rather than being cut off behind them. */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={cn('text-[15px] font-semibold text-[#101828] dark:text-zinc-50', selectableName)}>
                          {c.name}
                        </span>
                        {catName && <span className={cn(badgeBase, badgeNeutral)}>{catName}</span>}
                        <span className={cn(badgeBase, exists ? badgeNeutral : badgeGreen)}>
                          {exists ? t('productSetup.badgeExists') : t('productSetup.badgeNew')}
                        </span>
                      </div>
                      <div className="mt-2.5 text-[16px] leading-6 text-[#667085] dark:text-[#CBD5E1]">
                        {t('productSetup.sold30d', { count: c.qty30 })} •{' '}
                        {t('productSetup.lastSold', { date: shortDate(c.lastSold) })}
                      </div>
                    </SelectableRow>
                  );
                })}
              </div>
            )}
            {hiddenRevenueCount > 0 && (
              <p className={cn('text-xs', tertiaryText)}>
                {t('productSetup.moreResults', { count: hiddenRevenueCount })}
              </p>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="relative">
            {searchIcon}
            <input data-onboarding={practice ? "inventory-setup-search" : undefined}
              type="text"
              value={subrecipeSearch}
              onChange={(e) => onSubrecipeSearchChange(e.target.value)}
              placeholder={t('productSetup.searchSubrecipesPlaceholder') as string}
              className={inputClass}
            />
          </div>

          <div className="flex-1 p-4 rounded-lg border border-[#E8E8E9] dark:border-[#2D4F68] shadow-[0px_1px_1.5px_0.1px_#16192408] dark:shadow-[0px_1px_1.5px_0.1px_#00000040] flex flex-col min-h-0">
            <p className="text-[16px] leading-5 font-semibold text-[#0C111D] dark:text-zinc-50 flex items-center gap-1 mb-3 flex-shrink-0">
              {t('productSetup.subrecipesHeading')}
              <span>({subrecipeMatchCount})</span>
            </p>

            <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar min-h-0">
              {/* Create-new always sits on top — the product becomes its first ingredient. */}
              <SelectableRow isSelected={isCreateNewSelected} onClick={onToggleCreateNew}>
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold text-[#0E8965] dark:text-darkGreenColor-dark truncate">
                    + {t('productSetup.createNewSubrecipe')}
                  </span>
                </div>
                <div className="mt-2.5 text-[14px] leading-5 text-[#667085] dark:text-[#CBD5E1]">
                  {t('productSetup.createNewSubrecipeHint')}
                </div>
              </SelectableRow>

              {isLoadingSubrecipes ? (
                <p className={cn(smallTextSize, tertiaryText, 'py-6 text-center')}>{t('inventory.loading')}</p>
              ) : subrecipeCandidates.length === 0 ? (
                <p className={cn(smallTextSize, tertiaryText, 'py-6 text-center')}>{t('productSetup.noSubrecipes')}</p>
              ) : (
                subrecipeCandidates.map((s) => {
                  const unitDisplay = getPortionsUnitDisplay(s.portionsUnit);
                  return (
                    <SelectableRow
                      onboardingId={practice ? "inventory-setup-pick" : undefined}
                      key={s.id}
                      isSelected={isSubrecipeSelected(s.id)}
                      onClick={() => onToggleSubrecipe(s.id)}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={cn('text-[15px] font-semibold text-[#101828] dark:text-zinc-50', selectableName)}>
                          {s.name}
                        </span>
                        <span className={cn(badgeBase, s.usedInDishes > 0 ? badgeNeutral : badgeAmber)}>
                          {s.usedInDishes > 0
                            ? t('productSetup.usedInDishes', { count: s.usedInDishes })
                            : t('productSetup.subrecipeNotUsedYet')}
                        </span>
                      </div>
                      <div className="mt-2.5 text-[16px] leading-6 text-[#667085] dark:text-[#CBD5E1]">
                        {t('productSetup.subrecipeIngredients', { count: s.ingredientCount })} •{' '}
                        {t('productSetup.subrecipeMakes', { portions: s.portions, unit: unitDisplay })} •{' '}
                        {formatSubrecipeUnitPrice(s.unitCost, s.portionsUnit)}
                      </div>
                    </SelectableRow>
                  );
                })
              )}
            </div>
            {hiddenSubrecipeCount > 0 && (
              <p className={cn('text-xs', tertiaryText)}>
                {t('productSetup.moreResults', { count: hiddenSubrecipeCount })}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProductSetupSelectStep;
